import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { LS_Get, LS_Set } from '../utils/local-storage';
import { StatusBar, Style } from '@capacitor/status-bar';
import { IS_MOBILE } from '../data/js/server-constants';
import { trackError } from '../utils/error-tracking';

export type Theme = 'light' | 'dark';

export interface ThemeContextType {
  theme: Theme;
  /**
   * False until the provider has mounted on the client. `theme` is seeded from
   * the pre-hydration script, so on the first client render it can disagree with
   * what the server rendered; anything that turns `theme` into markup React
   * hydrates (text especially) has to wait for this flag or it trips a
   * hydration mismatch.
   */
  hydrated: boolean;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, defaultTheme = 'dark' }) => {
  // Seed from the value the pre-hydration script in _document resolved from
  // localStorage (window.__SELLWILD_THEME__). This makes the client's very first
  // render match the theme the script already painted, so there is no dark->light
  // flash and no hydration "revert". On the server window is undefined, so SSR
  // still renders `defaultTheme` (the div carries suppressHydrationWarning).
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window !== 'undefined') {
      const w = (window as unknown as { __SELLWILD_THEME__?: Theme }).__SELLWILD_THEME__;
      if (w === 'light' || w === 'dark') return w;
    }
    return defaultTheme as Theme;
  });
  const [hydrated, setHydrated] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const transitioningRef = useRef(transitioning);
  // Keep the ref synced with state so the toggleTheme event handler can read the
  // latest value without depending on it. Runs every time `transitioning` changes,
  // matching the prior every-render assignment timing for this read-in-handler ref.
  useEffect(() => {
    transitioningRef.current = transitioning;
  }, [transitioning]);

  const applyTheme = useCallback((newTheme: Theme) => {
    if (IS_MOBILE) {
      try {
        void StatusBar.setStyle({
          style: newTheme === 'dark' ? Style.Dark : Style.Light,
        });
      } catch (error) {
        trackError(error instanceof Error ? error : new Error('Error setting status bar style'), undefined, { component: 'ThemeProvider' });
      }
    }
    // Only effects and event handlers call this, and neither runs during SSR,
    // so `document` is always present here.
    document.documentElement.classList.remove('theme-light', 'theme-dark', 'dark');
    document.documentElement.classList.add(`theme-${newTheme}`);
    if (newTheme === 'dark') document.documentElement.classList.add('dark');
  }, []);

  useEffect(() => {
    // Initialize theme from localStorage or system preference
    const storedTheme = LS_Get('theme');
    const savedTheme: Theme = storedTheme === 'light' || storedTheme === 'dark' ? storedTheme : (() => {
      const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      const initialTheme: Theme = prefersDark ? 'dark' : 'light';
      LS_Set('theme', initialTheme);
      return initialTheme;
    })();

    // eslint-disable-next-line react-hooks/set-state-in-effect -- initial sync from localStorage/matchMedia (external systems unavailable during SSR) must run after mount to avoid hydration mismatch
    setThemeState(savedTheme);
    // Flips in the same commit as the theme above, so the first client render
    // still matches what the server sent.
    setHydrated(true);
    applyTheme(savedTheme);
  }, [applyTheme]);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    LS_Set('theme', newTheme);
    applyTheme(newTheme);
  }, [applyTheme]);

  const toggleTheme = useCallback(() => {
    if (transitioningRef.current) return;

    setThemeState(prev => {
      const newTheme = prev === 'dark' ? 'light' : 'dark';
      LS_Set('theme', newTheme);
      applyTheme(newTheme);
      return newTheme;
    });
    setTransitioning(true);

    setTimeout(() => {
      setTransitioning(false);
    }, 1000);
  }, [applyTheme]);

  const value: ThemeContextType = {
    theme,
    hydrated,
    toggleTheme,
    setTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export default ThemeProvider;